/* Phase 3 — Veteran self-service surface. Veterans see only their own
   cases: grant/decline sharing permission and self-report connection.
   Veteran self-report is sufficient evidence under the approved rules. */
import { useState, useEffect, useCallback } from "react";
import { Navigate, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, ShieldAlert, CheckCircle2, XCircle, MessageSquareHeart } from "lucide-react";
import Seo from "@/components/Seo";
import PilotDemoBanner from "@/components/PilotDemoBanner";
import { useUserAuth } from "@/context/UserAuthContext";
import { StateChip } from "@/pages/GuideWorkspace";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const btnGold = "inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-deep text-navy font-semibold px-4 py-2.5 rounded-md text-sm disabled:opacity-50 transition-colors";
const btnGhost = "inline-flex items-center justify-center gap-2 border border-navy/25 hover:border-navy text-navy font-semibold px-3 py-2 rounded-md text-xs transition-colors";

export default function MySupport() {
  const { user, loading, authHeaders } = useUserAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    const r = await axios.get(`${API}/coordination/my-cases`, { headers: authHeaders() });
    setData(r.data.cases || []);
  }, [authHeaders]);

  useEffect(() => { if (user) load().catch((e) => setErr(e?.response?.data?.detail || "Could not load your support cases.")); }, [user, load]);

  const decide = async (cid, granted) => {
    setErr(""); setMsg("");
    try {
      await axios.post(`${API}/coordination/my-cases/${cid}/permission`, { granted }, { headers: authHeaders() });
      setMsg(granted ? "Permission granted. Your WHO Guide can now make the introduction." : "Understood — nothing will be shared. This case has been closed.");
      load();
    } catch (ex) { setErr(ex?.response?.data?.detail || "Could not save your decision."); }
  };

  const report = async (cid, claim) => {
    setErr(""); setMsg("");
    const statement = claim === "connected"
      ? window.prompt("Anything you'd like your WHO Guide to know? (optional)") || undefined
      : window.prompt("Tell us what happened so your Guide can follow up: (optional)") || undefined;
    try {
      await axios.post(`${API}/coordination/my-cases/${cid}/evidence`, { claim, statement }, { headers: authHeaders() });
      setMsg(claim === "connected" ? "Thank you — your connection is confirmed." : "Thank you for telling us. Your WHO Guide will follow up.");
      load();
    } catch (ex) { setErr(ex?.response?.data?.detail || "Could not save your report."); }
  };

  if (loading) return <div className="min-h-screen bg-cream flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-gold-deep" /></div>;
  if (!user) return <Navigate to="/login" state={{ from: "/my-support" }} replace />;

  return (
    <div className="min-h-screen bg-cream" data-testid="my-support-page">
      <Seo title="My Support · WHO" path="/my-support" description="Your WHO coordination cases" />
      <PilotDemoBanner text="Pilot preview — cases shown here are synthetic demonstration records." />
      <div className="max-w-[860px] mx-auto px-6 lg:px-10 py-12">
        <p className="eyebrow text-gold-deep">My support</p>
        <h1 className="mt-3 font-display font-black text-navy text-3xl">Your coordination cases</h1>
        <p className="mt-2 text-sm text-slateSoft max-w-xl">
          Your WHO Guide coordinates each connection with your permission. Nothing is shared without your
          approval, and you can change your mind at any time in Settings.
        </p>
        {msg && <p className="mt-4 text-sm text-green-700 bg-green-50 border border-green-200 rounded-md px-3 py-2" data-testid="support-flash">{msg}</p>}
        {err && <p className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="support-error">{err}</p>}

        <div className="mt-8 space-y-4" data-testid="support-case-list">
          {!data && <Loader2 className="w-5 h-5 animate-spin text-gold-deep" />}
          {data?.length === 0 && (
            <div className="bg-white border border-borderSubtle rounded-lg p-8 text-center" data-testid="support-empty">
              <MessageSquareHeart className="w-8 h-8 text-gold-deep mx-auto" />
              <p className="mt-3 text-sm text-slateSoft">No coordination cases yet. When a WHO Guide opens one for you, it will appear here.</p>
            </div>
          )}
          {data?.map(({ case: c, provider, consent }) => (
            <div key={c.id} className="bg-white border border-borderSubtle rounded-lg p-5" data-testid={`support-case-${c.id}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <StateChip value={c.state} />
                {c.state === "closed" && c.closure_outcome && <span className="text-xs text-slateSoft capitalize">{c.closure_outcome.replace(/_/g, " ")}</span>}
              </div>
              <p className="mt-2 font-semibold text-navy">{c.need_summary}</p>
              {provider && <p className="mt-1 text-xs text-slateSoft">With: {provider.display_name}{provider.phone ? ` · ${provider.phone}` : ""}</p>}

              {c.state === "requesting_permission" && !consent?.granted && (
                <div className="mt-4 bg-gold/10 border border-gold/40 rounded-md p-4" data-testid={`permission-request-${c.id}`}>
                  <p className="text-sm text-navy font-semibold">Your WHO Guide would like to connect you with {provider?.display_name || "a provider"}.</p>
                  <p className="mt-1 text-xs text-slateSoft">May we share your name and contact information with them for this need only? You can withdraw this permission anytime.</p>
                  <div className="mt-3 flex gap-2">
                    <button className={btnGold} onClick={() => decide(c.id, true)} data-testid={`permission-grant-${c.id}`}><CheckCircle2 className="w-4 h-4" /> Yes, share my info</button>
                    <button className={btnGhost} onClick={() => decide(c.id, false)} data-testid={`permission-decline-${c.id}`}><XCircle className="w-3.5 h-3.5" /> No thanks</button>
                  </div>
                </div>
              )}

              {["provider_response", "confirming_connection", "bounded_recovery"].includes(c.state) && (
                <div className="mt-4 bg-cream-panel border border-borderSubtle rounded-md p-4">
                  <p className="text-sm text-navy font-semibold">Have you connected with {provider?.display_name || "the provider"}?</p>
                  <div className="mt-3 flex gap-2 flex-wrap">
                    <button className={btnGold} onClick={() => report(c.id, "connected")} data-testid={`report-connected-${c.id}`}>Yes, we connected</button>
                    <button className={btnGhost} onClick={() => report(c.id, "not_connected")} data-testid={`report-not-connected-${c.id}`}>Not yet</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <p className="mt-10 text-xs text-slateSoft">
          Manage your contact preferences and consents in <Link to="/settings" className="underline underline-offset-2 text-navy">Settings</Link>.
        </p>
      </div>
    </div>
  );
}
